import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { hasTasksAccess, hasFullAccess } from "../../../utils/planAccess";

// Reuse your existing DashboardCard component:
import DashboardCard from "../../../components/Cards/DashboardCard";

// Icons
import assign from "../../../assets/TaskManagement/assign.svg";
import inprogress from "../../../assets/TaskManagement/inprogress.svg";
import completed from "../../../assets/TaskManagement/completed.svg";
import delayed from "../../../assets/TaskManagement/delayed.svg";
import cancelled from "../../../assets/TaskManagement/cancelled.svg";
import TasksTable from "./DashboardTask.js"
import { decryptToken } from "../../../Utlis/Encryption.js";
import { BASE_URL } from "../../../components/BaseUrl.js";
import CalendarView from "./DashboardCalender.js"
import RequestManagement from "./DashboardRequest.js";
import MonthlySummary from "./DashboardPunches.js";
import TaskDetails from "../TaskMangement/MyTasks/TaskDetails.js";
import { useNavigate } from "react-router-dom";

import assignedtask from "../../../assets/TaskIcons/assignedtask.svg";
import pendingtask from "../../../assets/TaskIcons/pendingtask.svg";
import reimbursement from "../../../assets/TaskIcons/reimbursement.svg";
import payroll from "../../../assets/TaskIcons/payroll.svg";
import messages from "../../../assets/TaskIcons/messages.svg";
import documents from "../../../assets/TaskIcons/documents.svg";
import requests from "../../../assets/TaskIcons/requests.svg";


import taskicon1 from "../../../assets/LatestIcons/taskicon1.svg";
import taskicon2 from "../../../assets/LatestIcons/taskicon2.svg";
import taskicon3 from "../../../assets/LatestIcons/taskicon3.svg";
import taskicon4 from "../../../assets/LatestIcons/taskicon4.svg";
import taskicon5 from "../../../assets/LatestIcons/taskicon5.svg";
import taskicon6 from "../../../assets/LatestIcons/taskicon6.svg";
import taskicon7 from "../../../assets/LatestIcons/taskicon7.svg";
import taskicon8 from "../../../assets/LatestIcons/taskicon8.svg";
import taskicon9 from "../../../assets/LatestIcons/taskicon9.svg";
import taskicon10 from "../../../assets/LatestIcons/taskicon10.svg";
import taskicon11 from "../../../assets/LatestIcons/taskicon11.svg";
import taskicon12 from "../../../assets/LatestIcons/taskicon12.svg";
import taskicon13 from "../../../assets/LatestIcons/taskicon13.svg";
import icon16 from "../../../assets/LatestIcons/icon16.svg";
import icon17 from "../../../assets/LatestIcons/icon17.svg";
import icon20 from "../../../assets/LatestIcons/icon20.svg";

const Dashboard = () => {
  const { t } = useTranslation();
  const [selectedTask, setSelectedTask] = useState(null);
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const planId = user?.plan_id;
  const canSeeTasks = hasTasksAccess(planId);
  const canSeeRequests = hasFullAccess(planId);

  // Get token first
  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  const [stats, setStats] = useState({
    all_tasks: 0,
    completed_tasks: 0,
    assigned_task_until_today: 0,
    pending_task_until_today: 0,
    no_read_reimbursement_received_until_today: 0,
    no_read_payroll_received_until_today: 0,
    no_read_requests_answered_or_received_until_today: 0,
    no_read_received_messages_until_today: 0,
    no_read_received_documents_until_today: 0,
    no_read_received_notifications_until_today: 0,
    not_validated_events: 0,
  });

  // Fallback: Fetch tasks directly if counters API fails
  useEffect(() => {
    const fetchTasksAsFallback = async () => {
      if (!token) return;

      try {
        const res = await fetch(`${BASE_URL}/task-management/worker/tasks`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        console.log("✅ Tasks API fallback:", data);

        if (!data.error && data.data && data.data.tasks) {
          const tasks = data.data.tasks;
          const now = new Date();
          const today = now.toISOString().split('T')[0];

          // Calculate counters from tasks
          const assignedToday = tasks.filter(task => {
            const createdAt = new Date(task.created_at).toISOString().split('T')[0];
            return createdAt === today;
          }).length;

          const pending = tasks.filter(task =>
            task.status === 'assigned' || task.status === 'pending'
          ).length;
          const completed = tasks.filter(task => task.status === 'completed').length;

          setStats(prev => ({
            ...prev,
            all_tasks: tasks.length,
            completed_tasks: completed,
            assigned_task_until_today: assignedToday,
            pending_task_until_today: pending,
          }));
        }
      } catch (err) {
        console.error("❌ Error fetching tasks fallback:", err);
      }
    };

    // Only use fallback if counters API failed or returned no data
    const timer = setTimeout(() => {
      if (stats.assigned_task_until_today === 0 && stats.pending_task_until_today === 0) {
        console.log("🔄 Using tasks fallback to calculate counters...");
        fetchTasksAsFallback();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [token, stats.assigned_task_until_today, stats.pending_task_until_today]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [taskDetails, setTaskDetails] = useState(null);
  const fetchTaskDetails = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/task-management/worker/tasks/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      console.log("✅ Task details API:", data.data.task);

      if (!data.error) {
        // setTaskDetails(data.data.task); 
        setTaskDetails({
          ...data.data.task,
          assignments: data.data.assignments || [],
          comments: data.data.comments || []   // 👈 include comments here

        });
      }
    } catch (err) {
      console.error("❌ Error fetching task details:", err);
    }
  };



  useEffect(() => {
    const fetchCounters = async () => {
      try {
        const res = await fetch(`${BASE_URL}/task-management/worker/tasks/counters`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Check if response is JSON before parsing
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.warn("⚠️ Counters API returned non-JSON response:", contentType);
          // Try alternative endpoint or use fallback
          try {
            const altRes = await fetch(`${BASE_URL}/task-management/worker/tasks/count`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            const altData = await altRes.json();
            console.log("✅ Alternative Counters API:", altData);

            if (!altData.error && altData.data) {
              setStats(altData.data);
            }
          } catch (altErr) {
            console.error("❌ Error fetching from alternative endpoint:", altErr);
          }
          return;
        }

        const data = await res.json();
        console.log("✅ Counters API:", data);

        if (!data.error && data.data) {
          // Handle different response structures
          const counters = data.data.counters || data.data;
          setStats(counters);
        }
      } catch (err) {
        console.error("❌ Error fetching counters:", err);
      }
    };

    if (token) fetchCounters();
  }, [token]);



  const handleSelectTask = (id) => {
    setSelectedTaskId(id);
    fetchTaskDetails(id);
    const scrollContainer = document.querySelector("#root"); // or your main wrapper
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    setSelectedTaskId(null);
    setTaskDetails(null);
  };
  // const handleCardClick = () => {
  //   navigate("/my-tasks", { state: { fromCards: true, scrollTo: "tasks-table-section",   } });
  // };
  const handleCardClick = (status) => {
    navigate("/my-tasks", {
      state: {
        fromDashboard: true,
        scrollTo: "tasks-table-section",
        status, // 👈 pass status
      },
    });
  };
  return (
    <Box sx={{ px: 1 }}>
      {selectedTaskId && taskDetails ? (
        <TaskDetails
          task={taskDetails}
          onBack={handleBack}   // Pass back button handler
        />
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gap: 1,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
            }}
          >
            <DashboardCard
              icon={taskicon1}
              title={t("taskscards.allTasks")}
              description={stats.all_tasks}
              onClick={() =>
                navigate("/my-tasks", {
                  state: {
                    fromDashboard: true
                  },
                })
              }

              cardSx={{
                cursor: "pointer",
              }}
            // onClick={showUnderDevelopment}
            />
            <DashboardCard
              icon={taskicon2}
              title={t("taskscards.completedTasks")}
              description={stats.completed_tasks}
              onClick={() =>
                navigate("/my-tasks", {
                  state: {
                    fromDashboard: true
                  },
                })
              }

              cardSx={{
                cursor: "pointer",


              }}
            // onClick={showUnderDevelopment}
            />
            <DashboardCard
              icon={taskicon13}
              title={t("reimbursementReceived")}
              onClick={() =>
                navigate("/remuneration", {
                  state: {
                    fromDashboard: true
                  },
                })
              }
              cardSx={{
                cursor: "pointer",


              }}
              description={stats.no_read_reimbursement_received_until_today}

            />
            <DashboardCard
              icon={taskicon12}
              title={t("salaryReceived")}
              onClick={() =>
                navigate("/remuneration", {
                  state: {
                    fromDashboard: true
                  },
                })
              }
              description={stats.no_read_payroll_received_until_today}
              cardSx={{
                cursor: "pointer",
              }}
            />
            <DashboardCard
              icon={icon16}
              title={t("totalRequests")}
              onClick={() =>
                navigate("/request-management", {
                  state: {
                    fromDashboard: true
                  },
                })
              }
              cardSx={{
                cursor: "pointer",
              }}
              description={stats.no_read_requests_answered_or_received_until_today}
            />

            <DashboardCard
              icon={icon17}
              title={t("totalDocuments")}
              onClick={() =>
                navigate("/received-documents", {
                  state: {
                    fromDashboard: true
                  },
                })
              }
              description={stats.no_read_received_documents_until_today}
              cardSx={{
                cursor: "pointer",
              }}
            />

            <DashboardCard
              icon={icon20}
              title={t("totalMessages")}
              onClick={() =>
                navigate("/messages", {
                  state: {
                    fromDashboard: true
                  },
                })
              }
              description={stats.no_read_received_messages_until_today}
              cardSx={{
                cursor: "pointer",
              }}
            />

            {/* <DashboardCard
              icon={documents}
              title={t("totalNotifications")}
              onClick={() =>
                navigate("/notifications", {
                  state: {
                    fromDashboard: true
                  },
                })
              }
              description={stats.no_read_received_notifications_until_today}
              cardSx={{
                cursor: "pointer",
              }}
            /> */}

          </Box>

          {canSeeTasks && (
            <Box>
              <TasksTable onSelectTask={handleSelectTask} />
            </Box>
          )}
          {canSeeRequests && (
            <Box>
              <RequestManagement />
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <MonthlySummary />
          </Box>
        </>
      )}
    </Box>

  );
};

export default Dashboard;